import { Check, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import enFlag from "@/assets/images/en.png";
import ruFlag from "@/assets/images/ru.png";
import uzFlag from "@/assets/images/uz.png";
import { cn } from "@/lib/utils";

interface ChangeLanguageProps {
  type?: "dropdown" | "tab";
  align?: "start" | "center" | "end";
  className?: string;
  showIcon?: boolean;
  trigger?: React.ReactNode;
}

const languageItems: { value: string; label: string; flag: string }[] = [
  {
    value: "uz",
    label: "COMMON.UZBEK",
    flag: uzFlag,
  },
  {
    value: "ru",
    label: "COMMON.RUSSIAN",
    flag: ruFlag,
  },
  {
    value: "en",
    label: "COMMON.ENGLISH",
    flag: enFlag,
  },
];

export function ChangeLanguage({
  type = "dropdown",
  align = "end",
  className = "",
  showIcon = true,
  trigger,
}: Readonly<ChangeLanguageProps>) {
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language || "uz";

  // Saqlashni aniqlagichning o'zi bajaradi (`detection.caches`), shuning
  // uchun bu yerda qo'lda `localStorage` ga yozish YO'Q: ikkita yozuvchi
  // bo'lsa, kalit nomi yoki tartibi o'zgarganda ular ajralib ketardi.
  const handleChange = (lang: string) => {
    void i18n.changeLanguage(lang);
  };

  const getCurrentLanguageFlag = () => {
    const currentLang = languageItems.find(item => item.value === currentLanguage);
    if (currentLang && showIcon) {
      return (
        <img
          src={currentLang.flag}
          alt={t(currentLang.label)}
          className="h-4 w-4 rounded-sm object-cover"
        />
      );
    }
    return <Languages className="h-[1.2rem] w-[1.2rem]" />;
  };

  if (type === "tab") {
    return (
      <div className={`bg-muted inline-flex items-center rounded-lg border p-1 ${className}`}>
        {languageItems.map(item => (
          <button
            key={item.value}
            onClick={() => handleChange(item.value)}
            className={`inline-flex items-center justify-center rounded-md p-2 text-sm font-medium transition-all ${
              currentLanguage === item.value
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
            title={t(item.label)}
          >
            <img src={item.flag} alt={t(item.label)} className="h-4 w-4 rounded-sm object-cover" />
          </button>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger || (
          <Button variant="ghost" className={cn("relative flex justify-baseline", className)}>
            {getCurrentLanguageFlag()}
            {t("COMMON.CHANGE_LANGUAGE")}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-44">
        {languageItems.map(item => (
          <DropdownMenuItem
            key={item.value}
            onClick={() => handleChange(item.value)}
            className="flex cursor-pointer items-center gap-3 px-3 py-2"
          >
            <img src={item.flag} alt={t(item.label)} className="h-4 w-4 rounded-sm object-cover" />
            <span className="flex-1">{t(item.label)}</span>
            {currentLanguage === item.value && <Check className="text-primary h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
